import { useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminOrderStatusBadge, AdminPaymentStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminPermissionGate } from '../../components/primitives/admin-permission-gate.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatPaise, formatDateTime } from '../../lib/format.js';
import { useAdminOrders } from '../hooks/use-orders.js';
import { downloadAdminOrdersExport } from '../api/orders-api.js';
import { ORDER_STATUSES, PAYMENT_STATUSES, PAYMENT_METHODS, type AdminOrderSummary, type OrderListParams } from '../types/orders.js';

const PAGE_SIZE = 20;

export function OrdersListPage() {
  const { getToken } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [sortField, setSortField] = useState<OrderListParams['sortField']>('placedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const params: OrderListParams = useMemo(
    () => ({
      search: search || undefined,
      status: status ? [status as never] : undefined,
      paymentStatus: paymentStatus ? [paymentStatus as never] : undefined,
      paymentMethod: paymentMethod ? [paymentMethod as never] : undefined,
      dateFrom: dateFrom ? `${dateFrom}T00:00:00.000Z` : undefined,
      dateTo: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
      minTotalPaise: minTotal ? Number(minTotal) * 100 : undefined,
      maxTotalPaise: maxTotal ? Number(maxTotal) * 100 : undefined,
      sortField,
      sortDirection,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, status, paymentStatus, paymentMethod, dateFrom, dateTo, minTotal, maxTotal, sortField, sortDirection, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminOrders(params);
  const rows = data ?? [];

  function resetToFirstPage() {
    setPage(0);
  }

  async function handleExport() {
    setExportError(null);
    setExporting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Your session has expired. Please sign in again.');
      await downloadAdminOrdersExport(params, token);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  const columns: AdminDataTableColumn<AdminOrderSummary>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      sortable: true,
      render: (row) => (
        <Link
          to={`${ROUTE_NAMES.adminOrders}/${row.orderNumber}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {row.orderNumber}
        </Link>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.customerName ?? '—'}</p>
          <p className="text-xs text-gray-400">{row.customerEmail}</p>
        </div>
      ),
    },
    { key: 'itemCount', header: 'Items', render: (row) => row.itemCount },
    { key: 'totalPaise', header: 'Total', sortable: true, render: (row) => formatPaise(row.total.amountPaise) },
    { key: 'status', header: 'Status', render: (row) => <AdminOrderStatusBadge status={row.status} /> },
    {
      key: 'paymentStatus',
      header: 'Payment',
      render: (row) => (row.paymentStatus ? <AdminPaymentStatusBadge status={row.paymentStatus} /> : '—'),
    },
    { key: 'paymentMethod', header: 'Method', render: (row) => row.paymentMethod },
    { key: 'placedAt', header: 'Created', sortable: true, render: (row) => formatDateTime(row.createdAt) },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Orders"
        description="Search, review, and operate on customer orders."
        breadcrumbs={[{ label: 'Orders' }]}
        actions={
          <>
            <AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />
            <AdminPermissionGate anyRole={['ADMIN']}>
              <button
                type="button"
                onClick={() => void handleExport()}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </AdminPermissionGate>
          </>
        }
      />

      {exportError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {exportError}
        </p>
      )}

      <AdminSectionCard title="All orders" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              resetToFirstPage();
            }}
            placeholder="Search order number, customer, or email…"
            label="Search orders"
          />
          <AdminSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              resetToFirstPage();
            }}
            allLabel="All statuses"
            options={ORDER_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          />
          <AdminSelect
            label="Payment status"
            value={paymentStatus}
            onChange={(value) => {
              setPaymentStatus(value);
              resetToFirstPage();
            }}
            allLabel="All payment statuses"
            options={PAYMENT_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          />
          <AdminSelect
            label="Payment method"
            value={paymentMethod}
            onChange={(value) => {
              setPaymentMethod(value);
              resetToFirstPage();
            }}
            allLabel="All methods"
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
          />
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <label htmlFor="orders-date-from" className="sr-only">
              From date
            </label>
            <input
              id="orders-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                resetToFirstPage();
              }}
              className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
            <span aria-hidden="true">–</span>
            <label htmlFor="orders-date-to" className="sr-only">
              To date
            </label>
            <input
              id="orders-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value);
                resetToFirstPage();
              }}
              className="focus-visible:ring-brand rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <label htmlFor="orders-min-total" className="sr-only">
              Minimum total
            </label>
            <input
              id="orders-min-total"
              inputMode="numeric"
              placeholder="Min ₹"
              value={minTotal}
              onChange={(event) => {
                setMinTotal(event.target.value);
                resetToFirstPage();
              }}
              className="focus-visible:ring-brand w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
            <span aria-hidden="true">–</span>
            <label htmlFor="orders-max-total" className="sr-only">
              Maximum total
            </label>
            <input
              id="orders-max-total"
              inputMode="numeric"
              placeholder="Max ₹"
              value={maxTotal}
              onChange={(event) => {
                setMaxTotal(event.target.value);
                resetToFirstPage();
              }}
              className="focus-visible:ring-brand w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.orderNumber}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No orders match these filters"
          sort={{ field: sortField ?? 'placedAt', direction: sortDirection }}
          onSortChange={(field) => {
            if (field === sortField) {
              setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
            } else {
              setSortField(field as OrderListParams['sortField']);
              setSortDirection('desc');
            }
          }}
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
